import React, { useEffect, useMemo, useState } from "react";
import Icon from "../Icon";
import PillSelect from "./PillSelect";
import MockQuickFilter from "./MockQuickFilter";
import MockNewTicketModal from "./MockNewTicketModal";
import {
    TICKET_STATUS_OPTIONS,
    TICKET_PRIORITY_OPTIONS,
    buildDepartmentOptions,
} from "./mockData";

const MockTickets = ({
    tickets,
    onSelect,
    selectedId,
    statusFilter = [],
    departmentFilter = [],
    priorityFilter = [],
    assigneeFilter = [],
    searchQuery,
    onStatusFilterChange,
    onDepartmentFilterChange,
    onPriorityFilterChange,
    onAssigneeFilterChange,
    onSearchChange,
    departments = [],
    assigneeOptions = [],
    onStatusChange,
    onDepartmentChange,
    onPriorityChange,
    onAssigneeChange,
    onAddTicket,
    totalTickets = 0,
}) => {
    const [viewMode, setViewMode] = useState("list");
    const [activityTicket, setActivityTicket] = useState(null);
    const [showNewTicket, setShowNewTicket] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const totalPages = Math.max(1, Math.ceil(tickets.length / pageSize));
    const currentPage = Math.min(page, totalPages);

    useEffect(() => {
        setPage(1);
    }, [tickets.length, searchQuery, statusFilter, departmentFilter, priorityFilter, assigneeFilter]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const pagedTickets = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return tickets.slice(start, start + pageSize);
    }, [tickets, currentPage, pageSize]);
    const statusCellOptions = TICKET_STATUS_OPTIONS.filter((o) => o.value).map((o) => ({
        value: o.value,
        label: o.label,
        color: o.color,
    }));

    const statusQuickOptions = statusCellOptions;

    const deptCellOptions = useMemo(
        () => buildDepartmentOptions(departments),
        [departments]
    );

    const deptQuickOptions = useMemo(
        () => departments.map((d) => ({
            value: d.name,
            label: d.name,
            color: d.color,
        })),
        [departments]
    );

    const priorityQuickOptions = TICKET_PRIORITY_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        color: o.color,
    }));

    return (
        <div className="clk-mk-page clk-mk-page--tickets">
            <div className="clk-mk-page-header">
                <h5>Tickets</h5>
            </div>

            <div className="clk-mk-toolbar">
                <input
                    type="search"
                    className="clk-mk-search"
                    placeholder="Search Ticket"
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    aria-label="Search Ticket"
                />
                <div className="clk-mk-quick-filters">
                    <MockQuickFilter
                        label="Status"
                        options={statusQuickOptions}
                        value={statusFilter}
                        onChange={onStatusFilterChange}
                        ariaLabel="Filter by status"
                    />
                    <MockQuickFilter
                        label="Department"
                        options={deptQuickOptions}
                        value={departmentFilter}
                        onChange={onDepartmentFilterChange}
                        ariaLabel="Filter by department"
                    />
                    <MockQuickFilter
                        label="Priority"
                        options={priorityQuickOptions}
                        value={priorityFilter}
                        onChange={onPriorityFilterChange}
                        ariaLabel="Filter by priority"
                    />
                    <MockQuickFilter
                        label="Assignee"
                        options={assigneeOptions}
                        value={assigneeFilter}
                        onChange={onAssigneeFilterChange}
                        ariaLabel="Filter by assignee"
                    />
                </div>
                <div className="clk-mk-toolbar-actions">
                    <div className="clk-mk-action-group">
                        <button
                            type="button"
                            className={`clk-mk-action-btn${viewMode === "list" ? " is-active" : ""}`}
                            aria-label="List view"
                            onClick={() => setViewMode("list")}
                        >
                            <Icon name="menu" size={14} />
                        </button>
                        <button
                            type="button"
                            className={`clk-mk-action-btn${viewMode === "card" ? " is-active" : ""}`}
                            aria-label="Card view"
                            disabled
                            title="Not available in demo"
                        >
                            <Icon name="widget" size={14} />
                        </button>
                    </div>
                    <button
                        type="button"
                        className="clk-mk-action-btn"
                        aria-label="Export"
                        disabled
                        title="Not available in demo"
                    >
                        <Icon name="arrow" size={14} />
                    </button>
                    <button
                        type="button"
                        className="clk-mk-action-btn"
                        aria-label="Email summary"
                        disabled
                        title="Not available in demo"
                    >
                        <Icon name="mail" size={14} />
                    </button>
                    <button type="button" className="clk-mk-primary-btn" onClick={() => setShowNewTicket(true)}>
                        <img src="/images/plus.svg" alt="" aria-hidden="true" />
                        Add Ticket
                    </button>
                </div>
            </div>

            <div className="clk-mk-tickets-body">
                <div className="clk-mk-table-area">
                    {tickets.length === 0 ? (
                        <p className="clk-mk-empty">No tickets match these filters.</p>
                    ) : (
                        <div className="clk-mk-table-wrap">
                            <table className="clk-mk-ticket-table">
                        <thead>
                            <tr>
                                <th className="clk-mk-col-check"><span /></th>
                                <th className="clk-mk-col-ticket">Ticket</th>
                                <th className="clk-mk-col-select">Status</th>
                                <th className="clk-mk-col-select">Department</th>
                                <th className="clk-mk-col-select">Priority</th>
                                <th className="clk-mk-col-select">Assignee</th>
                                <th className="clk-mk-col-meta">Replied</th>
                                <th className="clk-mk-col-meta">Due</th>
                                <th className="clk-mk-col-meta">Created</th>
                                <th className="clk-mk-col-meta">Created By</th>
                                <th className="clk-mk-col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedTickets.map((t) => (
                                <tr
                                    key={t.id}
                                    className={selectedId === t.id ? "is-selected" : ""}
                                    onClick={() => onSelect(t)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === "Enter" && onSelect(t)}
                                >
                                    <td className="clk-mk-col-check" onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" aria-label={`Select ${t.title}`} />
                                    </td>
                                    <td className="clk-mk-col-ticket">
                                        <span className="clk-mk-tt">{t.title}</span>
                                        <span className="clk-mk-tp">{t.preview}</span>
                                    </td>
                                    <td className="clk-mk-col-select" onClick={(e) => e.stopPropagation()}>
                                        <PillSelect
                                            value={t.status}
                                            options={statusCellOptions}
                                            onChange={(val) => onStatusChange(t.id, val)}
                                            ariaLabel={`Status for ${t.title}`}
                                        />
                                    </td>
                                    <td className="clk-mk-col-select" onClick={(e) => e.stopPropagation()}>
                                        <PillSelect
                                            value={t.department}
                                            options={deptCellOptions}
                                            onChange={(val) => onDepartmentChange(t.id, val)}
                                            ariaLabel={`Department for ${t.title}`}
                                        />
                                    </td>
                                    <td className="clk-mk-col-select" onClick={(e) => e.stopPropagation()}>
                                        <PillSelect
                                            value={t.priorityKey}
                                            options={TICKET_PRIORITY_OPTIONS}
                                            onChange={(val) => onPriorityChange(t.id, val)}
                                            ariaLabel={`Priority for ${t.title}`}
                                        />
                                    </td>
                                    <td className="clk-mk-col-select" onClick={(e) => e.stopPropagation()}>
                                        <PillSelect
                                            value={t.assignee || ""}
                                            options={assigneeOptions}
                                            onChange={(val) => onAssigneeChange(t.id, val || null)}
                                            ariaLabel={`Assignee for ${t.title}`}
                                            showDot={Boolean(t.assignee)}
                                        />
                                    </td>
                                    <td className="clk-mk-col-meta">{t.replied || "-"}</td>
                                    <td className="clk-mk-col-meta">{t.due || "-"}</td>
                                    <td className="clk-mk-col-meta">{t.created}</td>
                                    <td className="clk-mk-col-meta">{t.createdBy}</td>
                                    <td className="clk-mk-col-actions" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            className="clk-mk-row-action"
                                            aria-label="Activity"
                                            title="Activity"
                                            onClick={() => setActivityTicket(t)}
                                        >
                                            <Icon name="activity" size={12} />
                                        </button>
                                        <button
                                            type="button"
                                            className="clk-mk-row-action"
                                            aria-label="View ticket"
                                            title="View Ticket"
                                            onClick={() => onSelect(t)}
                                        >
                                            <Icon name="eye" size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                        </div>
                    )}
                </div>

                <div className="clk-mk-table-footer">
                    <div className="clk-mk-pagination">
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                        <button
                            key={pageNumber}
                            type="button"
                            className={`clk-mk-page-btn${currentPage === pageNumber ? " is-active" : ""}`}
                            onClick={() => setPage(pageNumber)}
                        >
                            {pageNumber}
                        </button>
                    ))}
                </div>
                <div className="clk-mk-table-meta">
                    <label>
                        Page Size
                        <select
                            value={pageSize}
                            aria-label="Page size"
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                        </select>
                    </label>
                    <span>
                        Showing {pagedTickets.length} of {tickets.length || totalTickets} Tickets
                    </span>
                </div>
                </div>
            </div>

            {showNewTicket && (
                <MockNewTicketModal
                    departments={departments}
                    assigneeOptions={assigneeOptions}
                    onClose={() => setShowNewTicket(false)}
                    onSave={(data) => {
                        onAddTicket?.(data);
                        setShowNewTicket(false);
                    }}
                />
            )}

            {activityTicket && (
                <div className="clk-mk-modal-backdrop" onClick={() => setActivityTicket(null)}>
                    <div className="clk-mk-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="activity-title">
                        <h5 id="activity-title">Ticket activity</h5>
                        <p><strong>{activityTicket.title}</strong></p>
                        <ul className="clk-mk-activity-list">
                            <li>Ticket created on {activityTicket.created}</li>
                            <li>Status: {activityTicket.statusLabel || activityTicket.status}</li>
                            {activityTicket.assignee && <li>Assigned to {activityTicket.assignee}</li>}
                            {activityTicket.replied && activityTicket.replied !== "-" && (
                                <li>Last reply on {activityTicket.replied}</li>
                            )}
                        </ul>
                        <div className="clk-mk-create-actions">
                            <button type="button" className="clk-mk-primary-btn" onClick={() => setActivityTicket(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MockTickets;
